import { Children, cloneElement, useEffect, useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { CircleHelp } from 'lucide-react'
import { GLOSSARY } from '../pages/accounting/glossary'

// A small "?" that shows a plain explanation on hover, tap or keyboard
// focus, as in ASYS Tax's HelpTooltip. The page keeps its words short and
// the detail waits here. <HelpTip k="debit" /> reads the glossary;
// <HelpTip text="..." /> takes its own words.
export function HelpTip({ k, text, label = 'What does this mean?' }) {
  const body = text || GLOSSARY[k]
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState({ top: 0, left: 0, width: 280, placement: 'top' })
  const trigger = useRef(null)
  const tip = useRef(null)
  const id = useId()

  const place = () => {
    if (!trigger.current) return
    const rect = trigger.current.getBoundingClientRect()
    const width = Math.min(320, window.innerWidth - 24)
    const height = tip.current?.offsetHeight || 96
    const above = rect.top > window.innerHeight - rect.bottom && rect.top > height + 16
    const left = Math.min(Math.max(rect.left + rect.width / 2, 12 + width / 2), window.innerWidth - 12 - width / 2)
    const raw = above ? rect.top - height - 10 : rect.bottom + 10
    setPos({ top: Math.min(Math.max(raw, 12), window.innerHeight - height - 12), left, width, placement: above ? 'top' : 'bottom' })
  }

  useEffect(() => {
    if (!open) return undefined
    place()
    const frame = window.requestAnimationFrame(place)
    // A tap anywhere else closes it (phones have no mouse to leave with).
    const away = (e) => { if (!trigger.current?.contains(e.target)) setOpen(false) }
    window.addEventListener('resize', place)
    window.addEventListener('scroll', place, true)
    document.addEventListener('pointerdown', away, true)
    return () => {
      window.cancelAnimationFrame(frame)
      window.removeEventListener('resize', place)
      window.removeEventListener('scroll', place, true)
      document.removeEventListener('pointerdown', away, true)
    }
  }, [open, body])

  if (!body) return null
  return (
    <span className="help-tip-wrap" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button
        ref={trigger}
        type="button"
        className="help-tip-trigger"
        aria-label={label}
        aria-describedby={open ? id : undefined}
        aria-expanded={open}
        // Opens rather than toggles: a tap sends a mouse-enter first, so a
        // toggle would close it straight away.
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true) }}
        onFocus={(e) => { if (e.currentTarget.matches(':focus-visible')) setOpen(true) }}
        onBlur={() => setOpen(false)}
        onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false) }}
      >
        <CircleHelp size={14} aria-hidden="true" />
      </button>
      {open && createPortal(
        <span id={id} ref={tip} role="tooltip" className={`help-tip ${pos.placement}`} style={{ top: pos.top, left: pos.left, width: pos.width }}>
          {body}
        </span>,
        document.body,
      )}
    </span>
  )
}

/** A label followed by its "?". */
export function LabelWithHelp({ label, k, help }) {
  return (
    <span className="label-with-help">
      {label}
      <HelpTip k={k} text={help} label={`What is ${typeof label === 'string' ? label.toLowerCase() : 'this'}?`} />
    </span>
  )
}

/** A form field whose label has a "?": the label stays tied to the input
    (a "?" inside a <label> would take the label over), with an optional error. */
export function HelpField({ label, k, help, error, children }) {
  const id = useId()
  const control = Children.only(children)
  return (
    <div className="ci-form-field">
      <span className="label-with-help">
        <label htmlFor={id}>{label}</label>
        <HelpTip k={k} text={help} label={`What is ${typeof label === 'string' ? label.toLowerCase() : 'this'}?`} />
      </span>
      {cloneElement(control, { id })}
      {error && <span className="acc-field-error">{error}</span>}
    </div>
  )
}

export default HelpTip
